import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
  ExternalLink,
  Briefcase,
  Target,
  Calendar,
  Download,
  BarChart3,
  Send,
  CheckCircle,
  AlertCircle,
  Clock,
  Info,
  ArrowLeftRight,
  Landmark,
  type LucideIcon
} from 'lucide-react';
import { Tooltip } from '../components/ui/Tooltip';
import { Select } from '../components/ui/Select';
import { useAuth } from '../context/AuthContext';
import { useLumisial } from '../context/LumisialContext';
import { balanceService, PeriodoBalance, EvolucionMensualBalance } from '../services/balanceService';
import { notificacionBalanceService } from '../services/notificacionBalanceService';
import { NotificacionBalance } from '../lib/supabase';
import { NotificarBalanceModal } from '../components/NotificarBalanceModal';
import { EvolucionAnualTable } from '../components/EvolucionAnualTable';
import { DeudasSection } from '../components/DeudasSection';
import { deudaService } from '../services/deudaService';
import { movimientoService } from '../services/movimientoService';
import { reportarErrorInesperado } from '../utils/reportarErrorInesperado';
import { queryDePeriodo } from '../utils/periodoNavegacion';
import { puedeOperarDineroDeLumisial } from '../utils/permissions';
import { useAlCambiarClave } from '../hooks/useAlCambiarClave';
import { FondosCampanaTabla } from '../components/FondosCampanaTabla';

interface AccesoDetalleProps {
  readonly titulo: string;
  readonly descripcion: string;
  readonly aviso?: string;
  readonly icono: LucideIcon;
  readonly onClick: () => void;
  readonly dataTour?: string;
}

const AccesoDetalle: React.FC<AccesoDetalleProps> = ({ titulo, descripcion, aviso, icono: Icono, onClick, dataTour }) => (
  <div className="relative bg-white border border-slate-200 rounded-xl p-4 hover:border-slate-300 transition-colors" data-tour={dataTour}>
    <button
      type="button"
      onClick={onClick}
      className="absolute inset-0 rounded-xl cursor-pointer"
      aria-label={`Ver ${titulo}`}
    />
    <div className="relative flex items-center gap-3 pointer-events-none">
      <div className="p-2.5 bg-slate-100 rounded-lg shrink-0">
        <Icono className="w-5 h-5 text-slate-600" />
      </div>
      <div className="min-w-0">
        <p className="font-semibold text-slate-800">{titulo}</p>
        <p className="text-sm text-slate-500 truncate">{descripcion}</p>
        {aviso && <p className="text-sm font-semibold text-amber-600 mt-0.5">{aviso}</p>}
      </div>
    </div>
  </div>
);

function getEstadoIcon(estado: string) {
  switch (estado) {
    case 'enviado':
      return <Clock className="w-5 h-5 text-blue-600" />;
    case 'recibido':
      return <CheckCircle className="w-5 h-5 text-emerald-600" />;
    case 'observado':
      return <AlertCircle className="w-5 h-5 text-amber-600" />;
    default:
      return null;
  }
}

function getEstadoLabel(estado: string) {
  switch (estado) {
    case 'enviado':
      return 'Enviado';
    case 'recibido':
      return 'Recibido';
    case 'observado':
      return 'Observado';
    default:
      return estado;
  }
}

function getEstadoBadgeClass(estado: string) {
  switch (estado) {
    case 'enviado':
      return 'bg-blue-100 text-blue-800';
    case 'recibido':
      return 'bg-emerald-100 text-emerald-800';
    case 'observado':
      return 'bg-amber-100 text-amber-800';
    default:
      return 'bg-slate-100 text-slate-800';
  }
}

/** Mes cerrado: recién entonces se puede notificar su balance al nivel superior. */
function esPeriodoFinalizado(tipoPeriodo: 'mensual' | 'anual', año: number, mes: number): boolean {
  if (tipoPeriodo !== 'mensual') return false;
  return new Date() >= new Date(año, mes, 1);
}

interface EstadoRendicionProps {
  readonly notificacion: NotificacionBalance | null;
  readonly puedeGestionar: boolean;
  readonly periodoFinalizado: boolean;
  readonly onNotificar: () => void;
}

const EstadoRendicion: React.FC<EstadoRendicionProps> = ({
  notificacion, puedeGestionar, periodoFinalizado, onNotificar,
}) => {
  if (notificacion) {
    return (
      <div className="space-y-4">
        <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              {getEstadoIcon(notificacion.estado)}
              <span className="font-semibold text-slate-700">Estado de Notificación</span>
            </div>
            <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getEstadoBadgeClass(notificacion.estado)}`}>
              {getEstadoLabel(notificacion.estado)}
            </span>
          </div>
          
          <div className="text-sm text-slate-600 space-y-1">
            <p>Enviado: {new Date(notificacion.fecha_envio).toLocaleDateString('es-CL')}</p>
            {notificacion.nota && (
              <p className="mt-2 text-slate-700">
                <span className="font-medium">Nota:</span> {notificacion.nota}
              </p>
            )}
            {notificacion.nota_observacion && (
              <p className="mt-2 text-amber-700 bg-amber-50 p-2 rounded">
                <span className="font-medium">Observación:</span> {notificacion.nota_observacion}
              </p>
            )}
          </div>
        </div>

        {notificacion.estado === 'observado' && puedeGestionar && (
          <button
            onClick={onNotificar}
            className="mt-4 flex items-center gap-2 px-6 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors font-semibold"
          >
            <Send className="w-5 h-5" />
            Reenviar Balance Corregido
          </button>
        )}
      </div>
    );
  }

  if (periodoFinalizado && puedeGestionar) {
    return (
      <div className="space-y-4">
        <p className="text-slate-600">
          El período seleccionado ha finalizado. Puedes notificar el envío de este balance al nivel superior.
        </p>
        <button
          onClick={onNotificar}
          className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
        >
          <Send className="w-5 h-5" />
          Notificar Envío de Balance
        </button>
      </div>
    );
  }

  if (periodoFinalizado) {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
        <p className="text-slate-500">
          Este lumisial aún no ha notificado el envío de este balance.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
      <p className="text-amber-800">
        El período seleccionado aún no ha finalizado. Solo puedes notificar balances de meses completados.
      </p>
    </div>
  );
};

interface RendicionMensualProps {
  readonly notificacion: NotificacionBalance | null;
  readonly historial: NotificacionBalance[];
  readonly puedeGestionar: boolean;
  readonly periodoFinalizado: boolean;
  readonly onNotificar: () => void;
  readonly nombreMes: (mes: number) => string | undefined;
}

/** Tarjeta del mes: estado de su rendición y las últimas notificaciones del Lumisial. */
const RendicionMensual: React.FC<RendicionMensualProps> = ({
  notificacion, historial, puedeGestionar, periodoFinalizado, onNotificar, nombreMes,
}) => (
  <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-blue-100 p-2 rounded-lg">
          <Send className="w-6 h-6 text-blue-600" />
        </div>
        <h3 className="text-xl font-bold text-slate-800">Notificación de Balance</h3>
      </div>

      <EstadoRendicion
        notificacion={notificacion}
        puedeGestionar={puedeGestionar}
        periodoFinalizado={periodoFinalizado}
        onNotificar={onNotificar}
      />

      {historial.length > 0 && (
        <div className="mt-6 pt-6 border-t border-slate-200">
          <h4 className="font-semibold text-slate-700 mb-4">Historial de Notificaciones</h4>
          <div className="space-y-2">
            {historial.slice(0, 5).map((notif) => (
              <div
                key={notif.id}
                className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  {getEstadoIcon(notif.estado)}
                  <div>
                    <p className="font-medium text-slate-700">
                      {nombreMes(notif.periodo_mes)} {notif.periodo_anio}
                    </p>
                    <p className="text-sm text-slate-500">
                      {new Date(notif.fecha_envio).toLocaleDateString('es-CL')}
                    </p>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getEstadoBadgeClass(notif.estado)}`}>
                  {getEstadoLabel(notif.estado)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
);

/** Evolución mensual del año para el gráfico del Lumisial. */
function useEvolucionAnual(lumisialId: string, año: number) {
  const [evolucion, setEvolucion] = useState<EvolucionMensualBalance[]>([]);
  const [loadingEvolucion, setLoadingEvolucion] = useState(false);
  useAlCambiarClave(`${lumisialId}|${año}`, () => { setLoadingEvolucion(lumisialId !== ''); });

  useEffect(() => {
    if (!lumisialId) return;
    let vigente = true;
    balanceService.getEvolucionAnual(lumisialId, año)
      .then(data => { if (vigente) setEvolucion(data); })
      .catch((error: unknown) => { console.error('Error loading evolución anual:', error); })
      .finally(() => { if (vigente) setLoadingEvolucion(false); });
    return () => { vigente = false; };
  }, [lumisialId, año]);

  return { evolucion, loadingEvolucion };
}

/**
 * Rendición del mes elegido y las últimas del Lumisial. Cambiar de período limpia la mostrada
 * hasta que llegue la nueva; `recargarRendiciones` la vuelve a leer tras enviar o reenviar.
 */
function useRendicionesDelLumisial(lumisialId: string, tipoPeriodo: 'mensual' | 'anual', mes: number, año: number) {
  const [notificacionActual, setNotificacionActual] = useState<NotificacionBalance | null>(null);
  const [historialNotificaciones, setHistorialNotificaciones] = useState<NotificacionBalance[]>([]);
  const [recarga, setRecarga] = useState(0);
  useAlCambiarClave(`${lumisialId}|${tipoPeriodo}|${mes}|${año}`, () => {
    setNotificacionActual(null);
    setHistorialNotificaciones([]);
  });

  useEffect(() => {
    if (!lumisialId || tipoPeriodo !== 'mensual') return;
    let vigente = true;
    Promise.all([
      notificacionBalanceService.getByEntidadYPeriodo(lumisialId, mes, año),
      notificacionBalanceService.getHistorialPorEntidad(lumisialId)
    ])
      .then(([notifActual, historial]) => {
        if (!vigente) return;
        setNotificacionActual(notifActual);
        setHistorialNotificaciones(historial);
      })
      .catch((error: unknown) => { console.error('Error loading notificaciones:', error); });
    return () => { vigente = false; };
  }, [lumisialId, tipoPeriodo, mes, año, recarga]);

  return {
    notificacionActual,
    historialNotificaciones,
    recargarRendiciones: () => { setRecarga(n => n + 1); },
  };
}

const formatoCLP = new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' });
const formatCurrency = (amount: number) => formatoCLP.format(amount);

interface ResumenDelPeriodoProps {
  readonly periodoLabel: string;
  readonly ingresos: number;
  readonly egresos: number;
  /** Dinero prestado que entró a la cuenta: fuera de Ingresos Totales, dentro del Balance Neto. */
  readonly financiamiento: number;
  readonly balanceNeto: number;
}

const ResumenDelPeriodo: React.FC<ResumenDelPeriodoProps> = ({ periodoLabel, ingresos, egresos, financiamiento, balanceNeto }) => (
  <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl shadow-lg p-8 text-white">
    <div className="flex items-center gap-3 mb-6">
      <Calendar className="w-8 h-8" />
      <div>
        <h3 className="text-2xl font-bold">{periodoLabel}</h3>
        <p className="text-blue-100">Resumen del período</p>
      </div>
    </div>

    <div className={`grid grid-cols-1 gap-6 ${financiamiento > 0 ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
      <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
        <div className="flex items-center gap-2 mb-2">
          <TrendingUp className="w-5 h-5" />
          <span className="text-sm font-medium">Ingresos Totales</span>
        </div>
        <p className="text-3xl font-bold">{formatCurrency(ingresos)}</p>
      </div>

      <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
        <div className="flex items-center gap-2 mb-2">
          <TrendingDown className="w-5 h-5" />
          <span className="text-sm font-medium">Egresos Totales</span>
        </div>
        <p className="text-3xl font-bold">{formatCurrency(egresos)}</p>
      </div>

      {financiamiento > 0 && (
        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
          <div className="flex items-center gap-2 mb-2">
            <Landmark className="w-5 h-5" />
            <span className="text-sm font-medium">Financiamiento recibido</span>
          </div>
          <p className="text-3xl font-bold">{formatCurrency(financiamiento)}</p>
        </div>
      )}

      <div className={`backdrop-blur-sm rounded-lg p-6 ${
        balanceNeto >= 0 ? 'bg-emerald-500/20' : 'bg-red-500/20'
      }`}>
        <div className="flex items-center gap-2 mb-2">
          <DollarSign className="w-5 h-5" />
          <span className="text-sm font-medium">Balance Neto</span>
        </div>
        <p className={`text-3xl font-bold ${balanceNeto >= 0 ? 'text-emerald-100' : 'text-red-100'}`}>
          {formatCurrency(balanceNeto)}
        </p>
      </div>
    </div>
  </div>
);

interface ComparativaIngresosEgresosProps {
  readonly ingresos: number;
  readonly egresos: number;
}

const ComparativaIngresosEgresos: React.FC<ComparativaIngresosEgresosProps> = ({ ingresos, egresos }) => {
  const maxValue = Math.max(ingresos, egresos) || 1;
  return (
    <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-blue-100 p-2 rounded-lg">
          <BarChart3 className="w-6 h-6 text-blue-600" />
        </div>
        <h3 className="text-xl font-bold text-slate-800">Comparativa Ingresos vs Egresos</h3>
      </div>

      <div className="space-y-4">
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-slate-700">Ingresos</span>
            <span className="text-sm font-bold text-emerald-600">{formatCurrency(ingresos)}</span>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-8 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600 flex items-center justify-end px-3 text-white text-sm font-bold"
              style={{ width: `${(ingresos / maxValue) * 100}%` }}
            >
              {ingresos > 0 && `${((ingresos / maxValue) * 100).toFixed(0)}%`}
            </div>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-slate-700">Egresos</span>
            <span className="text-sm font-bold text-red-600">{formatCurrency(egresos)}</span>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-8 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-red-500 to-red-600 flex items-center justify-end px-3 text-white text-sm font-bold"
              style={{ width: `${(egresos / maxValue) * 100}%` }}
            >
              {egresos > 0 && `${((egresos / maxValue) * 100).toFixed(0)}%`}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Textos de las tarjetas de acceso al detalle. Fuera del componente: anidar los ternarios en el
 * JSX dispara la regla de complejidad del analizador y se lee peor.
 */
function textosAccesos(
  totalAportado: number,
  cantidadMovimientos: number,
  reembolsosPendientes: number,
  tipoPeriodo: 'mensual' | 'anual'
): { descripcionAportes: string; descripcionMovimientos: string; avisoReembolsos: string | undefined } {
  const sufijoAnual = tipoPeriodo === 'anual' ? ' en el año' : '';
  const descripcionAportes = totalAportado > 0
    ? `${formatCurrency(totalAportado)} aportado${sufijoAnual}`
    : 'Sin aportes este período';

  const pluralMovimientos = cantidadMovimientos === 1 ? '' : 's';
  const descripcionMovimientos = cantidadMovimientos > 0
    ? `${cantidadMovimientos} movimiento${pluralMovimientos}`
    : 'Sin movimientos este período';

  // El aviso de reembolso NO se acota al período filtrado: un reembolso pendiente no vence con el
  // mes, y esconderlo por estar mirando otro mes sería peor que mostrarlo. Por eso su texto no
  // menciona el período — se lee como alerta permanente, no como cifra del mes.
  const pluralReembolsos = reembolsosPendientes === 1 ? '' : 's';
  const avisoReembolsos = reembolsosPendientes > 0
    ? `${reembolsosPendientes} pendiente${pluralReembolsos} de reembolso`
    : undefined;

  return { descripcionAportes, descripcionMovimientos, avisoReembolsos };
}

function etiquetaPeriodo(tipoPeriodo: 'mensual' | 'anual', nombreMes: string | undefined, año: number): string {
  return tipoPeriodo === 'mensual' ? `${nombreMes} ${año}` : `Año ${año}`;
}

export const BalancePage: React.FC = () => {
  const navigate = useNavigate();
  const { usuario } = useAuth();
  const { selectedLumisial } = useLumisial();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const mesParam = searchParams.get('mes');
  const añoParam = searchParams.get('anio');
  const [mes, setMes] = useState(mesParam ? Number.parseInt(mesParam, 10) : new Date().getMonth() + 1);
  const [año, setAño] = useState(añoParam ? Number.parseInt(añoParam, 10) : new Date().getFullYear());
  const [showNotificarModal, setShowNotificarModal] = useState(false);

  const [balance, setBalance] = useState({
    ingresos: 0,
    egresos: 0,
    financiamiento: 0,
    balance: 0,
    cantidadMovimientos: 0,
    ingresosDesglosados: {
      aportesMensualesMiembros: 0,
      cuotaIglesiaRecaudada: 0,
      cuotaIglesiaJal: 0,
      cuotaIglesiaPA: 0,
      aportesExternos: 0,
      actividadesEconomicas: 0,
      campanas: 0
    },
    egresosDesglosados: {
      actividades: 0,
      gastosGenerales: 0,
      gastosLiturgicos: 0,
      gastosMisionales: 0,
      otros: 0
    }
  });

  const [campanasResumen, setCampanasResumen] = useState({ total: 0, activas: 0, totalRecaudado: 0 });
  const [actividadesResumen, setActividadesResumen] = useState({ total: 0, activas: 0, utilidadesTotales: 0 });
  const [deudaPendiente, setDeudaPendiente] = useState(0);
  const [reembolsosPendientes, setReembolsosPendientes] = useState(0);

  const tipoPeriodo = mes === 0 ? 'anual' : 'mensual';

  const años = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);
  const meses = [
    { value: 1, label: 'Enero' },
    { value: 2, label: 'Febrero' },
    { value: 3, label: 'Marzo' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Mayo' },
    { value: 6, label: 'Junio' },
    { value: 7, label: 'Julio' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Septiembre' },
    { value: 10, label: 'Octubre' },
    { value: 11, label: 'Noviembre' },
    { value: 12, label: 'Diciembre' }
  ];

  const lumisialId = selectedLumisial?.id ?? '';

  // Cambiar de Lumisial vuelve a mostrar la carga (se detecta durante el render).
  useAlCambiarClave(lumisialId, () => { setLoading(true); });
  const { evolucion, loadingEvolucion } = useEvolucionAnual(lumisialId, año);
  const {
    notificacionActual, historialNotificaciones, recargarRendiciones,
  } = useRendicionesDelLumisial(lumisialId, tipoPeriodo, mes, año);

  useEffect(() => {
    if (!selectedLumisial) return;
    let vigente = true;
    const periodo: PeriodoBalance = {
      tipo: tipoPeriodo,
      año: año,
      ...(tipoPeriodo === 'mensual' ? { mes } : {})
    };

    Promise.all([
      balanceService.getBalanceByPeriodo(selectedLumisial.id, periodo),
      balanceService.getCampanasResumen(selectedLumisial.id, periodo),
      balanceService.getActividadesResumen(selectedLumisial.id, periodo)
    ])
      .then(([balanceData, campanasData, actividadesData]) => {
        if (!vigente) return;
        setBalance(balanceData);
        setCampanasResumen(campanasData);
        setActividadesResumen(actividadesData);
      })
      .catch((error: unknown) => { console.error('Error loading balance:', error); })
      .finally(() => { if (vigente) setLoading(false); });

    // Independiente del balance/campañas/actividades: una falla acá no debe impedir
    // que el resto del período se muestre (evita acoplar un failure nuevo al bloque existente).
    deudaService.getSaldoPendienteEntidad({
      tipo: 'lumisial', id: selectedLumisial.id, nombre: selectedLumisial.nombre
    })
      .then(deudaPendienteData => { if (vigente) setDeudaPendiente(deudaPendienteData); })
      .catch((error: unknown) => { console.error('Error loading deuda pendiente:', error); });

    // El fallo se reporta en vez de tragarse: dejar el aviso en cero tras una consulta fallida
    // haría que la tarjeta afirme que no hay reembolsos pendientes cuando en realidad no lo sabemos.
    movimientoService.getGastosPendientesReembolso(selectedLumisial.id)
      .then(gastos => { if (vigente) setReembolsosPendientes(gastos.length); })
      .catch((error: unknown) => {
        if (vigente) setReembolsosPendientes(0);
        reportarErrorInesperado(error, 'No pudimos revisar si hay gastos pendientes de reembolso.');
      });
    return () => { vigente = false; };
  }, [selectedLumisial, tipoPeriodo, mes, año]);

  // Enviar la rendición, reenviarla y gestionar deudas: la JAL del Lumisial y la Junta Sacerdotal
  // superior (misma regla que puede_operar_dinero en la base).
  const puedeGestionarNotificacion = puedeOperarDineroDeLumisial(usuario, selectedLumisial);


  // Solo el aporte mensual: en el lumisial, "aportes" siempre significa aportes **para** el
  // lumisial. La cuota iglesia es dinero de paso hacia la diócesis y no se muestra en ningún
  // indicador de aportes — vive únicamente en su propia línea del desglose de ingresos, más abajo.
  // Sumarla acá hacía que esta ficha mostrara más que los "Ingresos Totales" de la misma pantalla.
  const { descripcionAportes, descripcionMovimientos, avisoReembolsos } = textosAccesos(
    balance.ingresosDesglosados.aportesMensualesMiembros,
    balance.cantidadMovimientos,
    reembolsosPendientes,
    tipoPeriodo
  );
  const queryDelPeriodo = queryDePeriodo({ mes, año });


  const observacionesPendientes = historialNotificaciones.filter(
    n => n.estado === 'observado' && !(n.periodo_mes === mes && n.periodo_anio === año)
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-slate-800 mb-2">Balance Financiero</h2>
          <p className="text-slate-600">Resumen de ingresos y egresos por período</p>
        </div>
      </div>

      {observacionesPendientes.map(n => (
        <div key={n.id} className="flex items-start justify-between gap-4 bg-amber-50 border border-amber-300 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-amber-800 text-sm">
                Balance observado — {meses.find(m => m.value === n.periodo_mes)?.label} {n.periodo_anio}
              </p>
              {n.nota_observacion && (
                <p className="text-amber-700 text-sm mt-0.5">{n.nota_observacion}</p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={() => { setMes(n.periodo_mes); setAño(n.periodo_anio); }}
            className="flex-shrink-0 text-sm font-semibold text-amber-700 hover:text-amber-900 underline underline-offset-2"
          >
            Ir al período
          </button>
        </div>
      ))}

      <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6">
        <div className="flex gap-4">
          <div className="flex-1">
            <Select
              label="Mes"
              options={[
                { value: '0', label: 'Todo el año' },
                ...meses.map((m) => ({ value: String(m.value), label: m.label })),
              ]}
              value={String(mes)}
              onChange={(v) => setMes(Number(v))}
              placeholder="Todo el año"
            />
          </div>
          <div className="flex-1">
            <Select
              label="Año"
              options={años.map((a) => ({ value: String(a), label: String(a) }))}
              value={String(año)}
              onChange={(v) => setAño(Number(v))}
              placeholder=""
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700"></div>
        </div>
      ) : (
        <>
          <ResumenDelPeriodo
            periodoLabel={etiquetaPeriodo(tipoPeriodo, meses.find(m => m.value === mes)?.label, año)}
            ingresos={balance.ingresos}
            egresos={balance.egresos}
            financiamiento={balance.financiamiento}
            balanceNeto={balance.balance}
          />

          {/* Accesos al detalle del balance. Van aquí, justo bajo el resumen del período: quien mira las
              cifras y necesita el detalle lo tiene a mano sin recorrer la página entera. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-tour="accesos-balance">
            <AccesoDetalle
              titulo="Aportes"
              icono={DollarSign}
              dataTour="acceso-aportes"
              descripcion={loading ? '—' : descripcionAportes}
              onClick={() => navigate(`/lumisial/balance/aportes${queryDelPeriodo}`)}
            />
            <AccesoDetalle
              titulo="Movimientos"
              icono={ArrowLeftRight}
              dataTour="acceso-movimientos"
              descripcion={loading ? '—' : descripcionMovimientos}
              aviso={avisoReembolsos}
              onClick={() => navigate(`/lumisial/balance/movimientos${queryDelPeriodo}`)}
            />
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingDown className="w-5 h-5 text-violet-600" />
              <span className="text-sm font-medium text-slate-600">Deuda pendiente</span>
            </div>
            <span className="text-lg font-bold text-slate-800">{formatCurrency(deudaPendiente)}</span>
          </div>

          {selectedLumisial && (
            <DeudasSection
              owner={{ tipo: 'lumisial', id: selectedLumisial.id, nombre: selectedLumisial.nombre }}
              soloLectura={!puedeGestionarNotificacion}
            />
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-emerald-100 p-2 rounded-lg">
                  <TrendingUp className="w-6 h-6 text-emerald-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-800">Desglose de Ingresos</h3>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Users className="w-5 h-5 text-blue-600" />
                    <span className="font-medium text-slate-700">Aportes Mensuales Miembros</span>
                  </div>
                  <span className="font-bold text-slate-800">
                    {formatCurrency(balance.ingresosDesglosados.aportesMensualesMiembros)}
                  </span>
                </div>

                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-amber-600" />
                      <span className="font-medium text-slate-700">Cuota Iglesia Recaudada</span>
                      <Tooltip content="La cuota iglesia es un fondo de tránsito: el lumisial la recauda y la transfiere a la diócesis. No suma al balance del lumisial." position="right">
                        <Info className="w-3.5 h-3.5 text-slate-400 cursor-help" />
                      </Tooltip>
                    </div>
                    <span className="font-bold text-amber-800">
                      {formatCurrency(balance.ingresosDesglosados.cuotaIglesiaRecaudada)}
                    </span>
                  </div>
                  <div className="flex gap-4 pl-8">
                    <div className="flex items-center gap-1.5">
                      <span className="px-1.5 py-0.5 bg-slate-200 text-slate-600 text-xs font-medium rounded">JAL</span>
                      <span className="text-sm text-slate-600">{formatCurrency(balance.ingresosDesglosados.cuotaIglesiaJal)}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="px-1.5 py-0.5 bg-slate-200 text-slate-600 text-xs font-medium rounded">PA</span>
                      <span className="text-sm text-slate-600">{formatCurrency(balance.ingresosDesglosados.cuotaIglesiaPA)}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <ExternalLink className="w-5 h-5 text-violet-600" />
                    <span className="font-medium text-slate-700">Aportes Externos</span>
                  </div>
                  <span className="font-bold text-slate-800">
                    {formatCurrency(balance.ingresosDesglosados.aportesExternos)}
                  </span>
                </div>

                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Briefcase className="w-5 h-5 text-orange-600" />
                    <span className="font-medium text-slate-700">Actividades Económicas</span>
                  </div>
                  <span className="font-bold text-slate-800">
                    {formatCurrency(balance.ingresosDesglosados.actividadesEconomicas)}
                  </span>
                </div>

                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Target className="w-5 h-5 text-pink-600" />
                    <span className="font-medium text-slate-700">Campañas</span>
                  </div>
                  <span className="font-bold text-slate-800">
                    {formatCurrency(balance.ingresosDesglosados.campanas)}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-red-100 p-2 rounded-lg">
                  <TrendingDown className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-800">Desglose de Egresos</h3>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Briefcase className="w-5 h-5 text-red-600" />
                    <span className="font-medium text-slate-700">Gastos de Actividades</span>
                  </div>
                  <span className="font-bold text-slate-800">
                    {formatCurrency(balance.egresosDesglosados.actividades)}
                  </span>
                </div>

                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <DollarSign className="w-5 h-5 text-blue-600" />
                    <span className="font-medium text-slate-700">Gastos Generales</span>
                  </div>
                  <span className="font-bold text-slate-800">
                    {formatCurrency(balance.egresosDesglosados.gastosGenerales)}
                  </span>
                </div>

                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <DollarSign className="w-5 h-5 text-purple-600" />
                    <span className="font-medium text-slate-700">Gastos Litúrgicos</span>
                  </div>
                  <span className="font-bold text-slate-800">
                    {formatCurrency(balance.egresosDesglosados.gastosLiturgicos)}
                  </span>
                </div>

                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <DollarSign className="w-5 h-5 text-green-600" />
                    <span className="font-medium text-slate-700">Gastos Misionales</span>
                  </div>
                  <span className="font-bold text-slate-800">
                    {formatCurrency(balance.egresosDesglosados.gastosMisionales)}
                  </span>
                </div>

                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <DollarSign className="w-5 h-5 text-slate-600" />
                    <span className="font-medium text-slate-700">Otros Egresos</span>
                  </div>
                  <span className="font-bold text-slate-800">
                    {formatCurrency(balance.egresosDesglosados.otros)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <ComparativaIngresosEgresos ingresos={balance.ingresos} egresos={balance.egresos} />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-pink-100 p-2 rounded-lg">
                  <Target className="w-6 h-6 text-pink-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-800">Campañas del Período</h3>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-sm text-slate-600 mb-1">Total Campañas</p>
                  <p className="text-3xl font-bold text-slate-800">{campanasResumen.total}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-sm text-slate-600 mb-1">Activas</p>
                  <p className="text-3xl font-bold text-emerald-600">{campanasResumen.activas}</p>
                </div>
              </div>

              <div className="mt-4 bg-pink-50 rounded-lg p-4">
                <p className="text-sm font-medium text-pink-600 mb-1">Recaudación monetaria del período</p>
                <p className="text-2xl font-bold text-slate-800">{formatCurrency(campanasResumen.totalRecaudado)}</p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-orange-100 p-2 rounded-lg">
                  <Briefcase className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-800">Actividades del Período</h3>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-sm text-slate-600 mb-1">Total Actividades</p>
                  <p className="text-3xl font-bold text-slate-800">{actividadesResumen.total}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-sm text-slate-600 mb-1">En Curso</p>
                  <p className="text-3xl font-bold text-emerald-600">{actividadesResumen.activas}</p>
                </div>
              </div>

              <div className="mt-4 bg-orange-50 rounded-lg p-4">
                <p className="text-sm font-medium text-orange-600 mb-1">Utilidades Generadas</p>
                <p className={`text-2xl font-bold ${actividadesResumen.utilidadesTotales >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {formatCurrency(actividadesResumen.utilidadesTotales)}
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={() => alert('Funcionalidad de exportar PDF en desarrollo')}
              className="flex items-center gap-2 px-6 py-3 bg-slate-700 text-white rounded-lg hover:bg-slate-800 transition-colors font-semibold"
            >
              <Download className="w-5 h-5" />
              Exportar PDF
            </button>
          </div>

          {selectedLumisial && <FondosCampanaTabla
            ownerContext={{ tipo: 'lumisial', id: selectedLumisial.id, nombre: selectedLumisial.nombre }}
            puedeOperar={puedeGestionarNotificacion}
            periodo={{ tipo: tipoPeriodo, año, ...(tipoPeriodo === 'mensual' ? { mes } : {}) }}
          />}
          <EvolucionAnualTable
            datos={evolucion}
            loading={loadingEvolucion}
            año={año}
            mesSeleccionado={tipoPeriodo === 'mensual' ? mes : undefined}
            onMesClick={(m) => setMes(m)}
          />

          {tipoPeriodo === 'mensual' && (
            <RendicionMensual
              notificacion={notificacionActual}
              historial={historialNotificaciones}
              puedeGestionar={puedeGestionarNotificacion}
              periodoFinalizado={esPeriodoFinalizado(tipoPeriodo, año, mes)}
              onNotificar={() => { setShowNotificarModal(true); }}
              nombreMes={(m) => meses.find(x => x.value === m)?.label}
            />
          )}
        </>
      )}

      {puedeGestionarNotificacion && (
        <NotificarBalanceModal
          isOpen={showNotificarModal}
          onClose={() => setShowNotificarModal(false)}
          onSuccess={() => {
            setShowNotificarModal(false);
            recargarRendiciones();
          }}
          entidadId={selectedLumisial?.id || ''}
          tipoEntidad="lumisial"
          mes={mes}
          año={año}
          mode={notificacionActual?.estado === 'observado' ? 'reenviar' : 'enviar'}
          notificacionId={notificacionActual?.id}
        />
      )}
    </div>
  );
};
